import constants from "Services/constants"

export const PRODUCT_LOCAL_STORAGE_KEY = "clouderp-product"
export const PRODUCT_SWITCH_ORGANIZATION_PARAM = "__clouderp_switch_org__"
export const PRODUCT_SWITCH_FROM_PARAM = "__clouderp_switch_from_product__"
export const PRODUCT_SWITCH_ONCE_PARAM = "__clouderp_switch_once__"

const VALID_PRODUCTS = [
  constants.ORGANIZATION_PRODUCT_CLOUDERP,
  constants.ORGANIZATION_PRODUCT_PROFITDRIVE,
]
const PRODUCT_DOMAINS = {
  [constants.ORGANIZATION_PRODUCT_CLOUDERP]: "clouderp.hu",
  [constants.ORGANIZATION_PRODUCT_PROFITDRIVE]: "profitdrive.ai",
}

function splitHostAndPort (host) {
  const separatorIndex = host.indexOf(":")
  if (separatorIndex === -1) return { hostname: host, port: "" }
  return {
    hostname: host.slice(0, separatorIndex),
    port: host.slice(separatorIndex),
  }
}

function hasDomainSuffix (hostname, domain) {
  return hostname === domain || hostname.endsWith(`.${domain}`)
}

function remapHostDomain (host, sourceDomain, targetDomain) {
  const { hostname, port } = splitHostAndPort(host)
  if (!hasDomainSuffix(hostname, sourceDomain)) return null
  if (hostname === sourceDomain) return `${targetDomain}${port}`
  const subdomain = hostname.slice(0, -(sourceDomain.length + 1))
  return `${subdomain}.${targetDomain}${port}`
}

export function normalizeProduct (product) {
  if (!product || typeof product !== "string") return null
  const normalizedProduct = product.toLowerCase()
  return VALID_PRODUCTS.includes(normalizedProduct) ? normalizedProduct : null
}

export function getProductFromHost (host = window.location.host) {
  const { hostname } = splitHostAndPort(host)
  if (hasDomainSuffix(hostname, PRODUCT_DOMAINS[constants.ORGANIZATION_PRODUCT_PROFITDRIVE])) {
    return constants.ORGANIZATION_PRODUCT_PROFITDRIVE
  }
  if (hasDomainSuffix(hostname, PRODUCT_DOMAINS[constants.ORGANIZATION_PRODUCT_CLOUDERP])) {
    return constants.ORGANIZATION_PRODUCT_CLOUDERP
  }
  return host.includes("profitdrive")
    ? constants.ORGANIZATION_PRODUCT_PROFITDRIVE
    : constants.ORGANIZATION_PRODUCT_CLOUDERP
}

export function getCurrentProduct () {
  if (process.env.NODE_ENV !== "production") {
    const localStorageProduct = normalizeProduct(window.localStorage.getItem(PRODUCT_LOCAL_STORAGE_KEY))
    if (localStorageProduct) return localStorageProduct
  }
  return getProductFromHost(window.location.host)
}

export function getHostForProduct (product, host = window.location.host) {
  const normalizedProduct = normalizeProduct(product)
  if (!normalizedProduct) return null
  const currentProduct = getProductFromHost(host)
  if (normalizedProduct === currentProduct) return host
  const sourceDomain = PRODUCT_DOMAINS[currentProduct]
  const targetDomain = PRODUCT_DOMAINS[normalizedProduct]
  if (!sourceDomain || !targetDomain) return null
  return remapHostDomain(host, sourceDomain, targetDomain)
}

export function getOrganizationSwitchParams () {
  const search = new URLSearchParams(window.location.search)
  return {
    organizationSlug: search.get(PRODUCT_SWITCH_ORGANIZATION_PARAM),
    fromProduct: normalizeProduct(search.get(PRODUCT_SWITCH_FROM_PARAM)),
    once: search.get(PRODUCT_SWITCH_ONCE_PARAM),
  }
}

export function clearOrganizationSwitchParams () {
  const search = new URLSearchParams(window.location.search)
  search.delete(PRODUCT_SWITCH_ORGANIZATION_PARAM)
  search.delete(PRODUCT_SWITCH_FROM_PARAM)
  search.delete(PRODUCT_SWITCH_ONCE_PARAM)
  const queryString = search.toString()
  const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname
  window.history.replaceState({}, "", nextUrl + window.location.hash)
}

export function getCrossProductSwitchUrl ({
  organizationSlug,
  targetProduct,
}) {
  const targetHost = getHostForProduct(targetProduct, window.location.host)
  if (!targetHost) return null
  const nextUrl = new URL(window.location.href)
  nextUrl.host = targetHost
  nextUrl.searchParams.set(PRODUCT_SWITCH_ORGANIZATION_PARAM, organizationSlug)
  nextUrl.searchParams.set(PRODUCT_SWITCH_FROM_PARAM, getCurrentProduct())
  nextUrl.searchParams.set(PRODUCT_SWITCH_ONCE_PARAM, Date.now().toString())
  return nextUrl.toString()
}
